import {ChevronDown, FileCheck2, Search, X} from 'lucide-react'
import React, {FC, memo, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

/** `id`: the teacher's `user_teacher.id` in the server flow, which saves by id. */
export type AllocationTeacherOption = {name: string; employeeId?: string; id?: number}

type Props = {
  teachers: AllocationTeacherOption[]
  value: string
  selectedEmployeeId?: string
  /** The saved selection came from an uploaded file (source marker, not an approval state). */
  imported?: boolean
  disabled?: boolean
  /** Accessible name: "Teacher for English, Grade 6-A". */
  ariaLabel: string
  /** `null` clears the selection (the subject stays, unassigned). */
  onSelect: (teacher: AllocationTeacherOption | null) => void
}

const DROPDOWN_MAX_HEIGHT = 240

type MenuProps = {
  anchor: HTMLButtonElement
  teachers: AllocationTeacherOption[]
  value: string
  selectedEmployeeId?: string
  ariaLabel: string
  onChoose: (teacher: AllocationTeacherOption | null) => void
  onClose: () => void
}

/**
 * The open list: search, options and "Clear teacher selection". Mounted only while open, so a
 * grid of hundreds of cells carries one light trigger button per cell and no list state.
 */
const TeacherMenu: FC<MenuProps> = ({
  anchor,
  teachers,
  value,
  selectedEmployeeId,
  ariaLabel,
  onChoose,
  onClose,
}) => {
  const intl = useIntl()
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [position] = useState(() => {
    const rect = anchor.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const above = spaceBelow < DROPDOWN_MAX_HEIGHT + 60 && rect.top > spaceBelow
    return {
      top: above ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
      above,
    }
  })

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return teachers
    return teachers.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.employeeId ?? '').toLowerCase().includes(q)
    )
  }, [query, teachers])

  useEffect(() => {
    inputRef.current?.focus()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (anchor.contains(target) || listRef.current?.contains(target)) return
      onClose()
    }
    // The list is fixed-positioned: close it rather than let it drift when the grid scrolls.
    const onScroll = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onClose)
    }
  }, [anchor, onClose])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[highlighted]) onChoose(filtered[highlighted])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      anchor.focus()
    }
  }

  const isSelected = (t: AllocationTeacherOption) =>
    t.name === value && (!selectedEmployeeId || (t.employeeId ?? '') === selectedEmployeeId)
  const searchLabel = intl.formatMessage({
    id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.GRID.SEARCH_TEACHERS',
  })

  return createPortal(
    <div
      ref={listRef}
      className='ta-teacher-menu'
      style={{
        top: position.above ? undefined : position.top,
        bottom: position.above ? window.innerHeight - position.top : undefined,
        left: position.left,
        width: position.width,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className='ta-teacher-menu__search'>
        <Search size={14} aria-hidden />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setHighlighted(0)
          }}
          onKeyDown={onKeyDown}
          placeholder={searchLabel}
          aria-label={searchLabel}
        />
      </div>
      <div className='ta-teacher-menu__list' role='listbox' aria-label={ariaLabel}>
        {filtered.length === 0 && (
          <div className='ta-teacher-menu__empty'>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.GRID.NO_TEACHER'})}
          </div>
        )}
        {filtered.map((t, idx) => (
          <button
            type='button'
            role='option'
            aria-selected={isSelected(t)}
            key={`${t.name}|${t.employeeId ?? ''}|${idx}`}
            className={`ta-teacher-menu__option${idx === highlighted ? ' is-active' : ''}${
              isSelected(t) ? ' is-selected' : ''
            }`}
            onMouseEnter={() => setHighlighted(idx)}
            onClick={() => onChoose(t)}
          >
            <span>{t.name}</span>
            {t.employeeId && <span className='ta-teacher-menu__id'>{t.employeeId}</span>}
          </button>
        ))}
      </div>
      {value.trim() && (
        <button type='button' className='ta-teacher-menu__clear' onClick={() => onChoose(null)}>
          <X size={14} aria-hidden />
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.GRID.CLEAR_TEACHER'})}
        </button>
      )}
    </div>,
    document.body
  )
}

/**
 * Searchable teacher picker for one grid cell (handover, Point 3 "Editing and navigation"): a
 * selection saves immediately, "Clear teacher selection" leaves the subject unassigned, and
 * clicking the picker never reaches the cell underneath (which opens the cell settings).
 */
const AllocationTeacherSelect: FC<Props> = ({
  teachers,
  value,
  selectedEmployeeId,
  imported,
  disabled,
  ariaLabel,
  onSelect,
}) => {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const hasValue = value.trim().length > 0

  const close = () => setOpen(false)
  const choose = (teacher: AllocationTeacherOption | null) => {
    onSelect(teacher)
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className={`ta-teacher-select${hasValue ? ' is-filled' : ''}${open ? ' is-open' : ''}`}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(true)
          }
        }}
      >
        {hasValue ? (
          imported ? (
            <FileCheck2
              size={15}
              className='ta-teacher-select__icon'
              aria-label={intl.formatMessage({
                id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.GRID.IMPORTED_ICON',
              })}
            />
          ) : null
        ) : (
          <Search size={14} className='ta-teacher-select__icon' aria-hidden />
        )}
        <span className='ta-teacher-select__value' title={hasValue ? value : undefined}>
          {hasValue
            ? value
            : intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.GRID.SELECT_TEACHER'})}
        </span>
        <ChevronDown size={15} className='ta-teacher-select__chevron' aria-hidden />
      </button>
      {open && triggerRef.current && (
        <TeacherMenu
          anchor={triggerRef.current}
          teachers={teachers}
          value={value}
          selectedEmployeeId={selectedEmployeeId}
          ariaLabel={ariaLabel}
          onChoose={choose}
          onClose={close}
        />
      )}
    </>
  )
}

export default memo(AllocationTeacherSelect)
